<script>
import { pushState, enablePush } from '@/push'
import { pwaState } from '@/pwa'

const SNOOZE_KEY = 'push_prompt_until'
// «Позже»: снова предложим через двое суток; после установки приложения предлагаем сразу
const SNOOZE_MS = 2 * 24 * 3600 * 1000

function snoozedUntil() {
  try {
    return Number(localStorage.getItem(SNOOZE_KEY)) || 0
  } catch (e) {
    return 0
  }
}

// Приглашение включить уведомления: всем, чей браузер умеет push, сервер их настроил, а разрешение ещё не спрашивали
// (в том числе после установки приложения на экран устройства). Разрешение просим по нажатию кнопки: на iPhone и в части браузеров иначе нельзя.
export default {
  name: 'PushPrompt',
  data() {
    return { snooze: snoozedUntil(), busy: false, error: '', now: Date.now() }
  },
  computed: {
    push() {
      return pushState
    },
    pwa() {
      return pwaState
    },
    // Предлагаем, если браузер умеет push, сервер настроен, разрешение не запрещено, а подписки нет
    visible() {
      const p = this.push
      return p.ready && p.supported && p.serverEnabled && p.permission !== 'denied' && !p.subscribed && this.now >= this.snooze
    },
    // Установленное приложение: уведомления ему особенно нужны
    text() {
      return this.pwa.standalone
        ? 'Включите уведомления, чтобы получать сообщения об обращениях, ремонтах и заявках прямо на устройство.'
        : 'Разрешите уведомления, чтобы не пропускать обращения водителей, заявки на ремонт и важные изменения.'
    }
  },
  watch: {
    // приложение только что установлено — предлагаем сразу, даже если ранее отложили
    'pwa.standalone'(on) {
      if (on) this.reset()
    }
  },
  methods: {
    reset() {
      this.snooze = 0
      this.now = Date.now()
      try {
        localStorage.removeItem(SNOOZE_KEY)
      } catch (e) {
        // без запоминания приглашение просто покажется снова
      }
    },
    later() {
      this.snooze = Date.now() + SNOOZE_MS
      try {
        localStorage.setItem(SNOOZE_KEY, String(this.snooze))
      } catch (e) {
        // без запоминания приглашение покажется при следующем входе
      }
    },
    enable() {
      if (this.busy) return
      this.busy = true
      this.error = ''
      enablePush()
        .catch((err) => {
          this.error = (err && err.message) || 'Не удалось включить уведомления'
          // отказ в окне браузера: не пристаём, пока человек сам не включит их в кабинете
          if (this.push.permission === 'denied') this.later()
        })
        .finally(() => { this.busy = false })
    }
  }
}
</script>

<template>
  <div v-if="visible" class="pp" role="dialog" aria-label="Включить уведомления">
    <div class="pp__icon"><i class="fas fa-bell"></i></div>
    <div class="pp__body">
      <strong>Включить уведомления?</strong>
      <span>{{ text }}</span>
      <span v-if="error" class="pp__error">{{ error }}</span>
    </div>
    <div class="pp__actions">
      <button type="button" class="pp__btn pp__btn--primary" :disabled="busy" @click="enable">{{ busy ? 'Включаю…' : 'Включить' }}</button>
      <button type="button" class="pp__btn" @click="later">Позже</button>
    </div>
  </div>
</template>

<style scoped>
.pp {
  position: fixed;
  left: 50%;
  bottom: var(--space-10);
  transform: translateX(-50%);
  z-index: 2600;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  width: min(560px, calc(100vw - 2 * var(--space-8)));
  box-sizing: border-box;
  padding: var(--space-8);
  background: var(--bg-surface);
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
}
.pp__icon { flex-shrink: 0; display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%; background: var(--primary-soft-2, #eef3ff); color: var(--primary); font-size: var(--text-lg); }
.pp__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: var(--space-2); font-size: var(--text-sm); color: var(--text-secondary); }
.pp__body strong { color: var(--text-primary); font-size: var(--text-base); }
.pp__error { color: var(--danger-text); }
.pp__actions { flex-shrink: 0; display: flex; flex-direction: column; gap: var(--space-3); }
.pp__btn { padding: var(--space-4) var(--space-8); font: inherit; font-size: var(--text-sm); color: var(--text-secondary); background: var(--bg-surface); border: var(--border-width-default) solid var(--border-default); border-radius: var(--radius-md); cursor: pointer; white-space: nowrap; }
.pp__btn:hover { background: var(--bg-subtle); }
.pp__btn--primary { color: #fff; background: var(--primary); border-color: var(--primary); }
.pp__btn--primary:hover { background: var(--primary); filter: brightness(1.08); }
.pp__btn:disabled { opacity: 0.6; cursor: default; }
@media (max-width: 480px) {
  .pp { flex-wrap: wrap; bottom: var(--space-6); }
  .pp__actions { flex-direction: row; width: 100%; }
  .pp__btn { flex: 1; }
}
</style>
